import { ModalActionsEnum } from '../DocumentTableConstants';
import { DocumentPermissions } from '../../../pages/document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { DocumentStatusEnum } from '../../../shared/constants/document';
import { DocumentSourceEnum } from '../../document-wizard/document-editor/constants/EditorConstats';

type PermissionCheckProps = {
  action: string;
  permissions?: DocumentPermissions;
  isInvitationAccepted?: boolean;
  status?: string | null;
  isDocumentCreator: boolean;
  currentTeamPossession: boolean;
  receiverTeam?: null | { id: string };
  documentSource: DocumentSourceEnum;
};

export const checkDropdownActionAvailability = ({
  action,
  permissions,
  status: documentStatus,
  isDocumentCreator,
  currentTeamPossession,
  receiverTeam,
  documentSource,
}: PermissionCheckProps): boolean => {
  const isDocumentStatusMatch = (currentDocumentStatus: string): boolean => {
    return documentStatus === currentDocumentStatus;
  };

  const hasUserInvitePermission = !!permissions?.hasInviteSendingPermission;
  const hasUserEditPermission = !!permissions?.hasEditPermission;

  switch (action) {
    case ModalActionsEnum.UNSEND:
      return (
        isDocumentCreator &&
        hasUserInvitePermission &&
        (isDocumentStatusMatch(DocumentStatusEnum.REVIEWING) ||
         isDocumentStatusMatch(DocumentStatusEnum.SHARED) ||
         (isDocumentStatusMatch(DocumentStatusEnum.EXECUTED) && !!receiverTeam))
      );

    case ModalActionsEnum.SEND:
      // Allow any user with edit permission and possession to send their documents
      const canSendWithEditPermission =
        hasUserEditPermission &&
        currentTeamPossession &&
        (isDocumentStatusMatch(DocumentStatusEnum.DRAFT) ||
          isDocumentStatusMatch(DocumentStatusEnum.UPLOADED) ||
          isDocumentStatusMatch(DocumentStatusEnum.SHARED) ||
          isDocumentStatusMatch(DocumentStatusEnum.REVIEWING));

      // Original logic for users with invite permission
      const isReviewingDocumentAvailableToBeSent =
        hasUserInvitePermission && currentTeamPossession && isDocumentStatusMatch(DocumentStatusEnum.REVIEWING);
      const isDraftDocumentAvailableToBeSent =
        hasUserInvitePermission &&
        isDocumentCreator &&
        (isDocumentStatusMatch(DocumentStatusEnum.DRAFT) ||
          isDocumentStatusMatch(DocumentStatusEnum.UPLOADED) ||
          isDocumentStatusMatch(DocumentStatusEnum.SHARED));

      return canSendWithEditPermission || isReviewingDocumentAvailableToBeSent || isDraftDocumentAvailableToBeSent;

    case ModalActionsEnum.TERMINATE:
      return hasUserEditPermission && isDocumentCreator && !isDocumentStatusMatch(DocumentStatusEnum.EXECUTED);

    case ModalActionsEnum.DOWNLOAD:
      return isDocumentStatusMatch(DocumentStatusEnum.EXECUTED) || documentSource === DocumentSourceEnum.IMPORTED;

    case ModalActionsEnum.SEND_FOR_SIGNING:
      return (
        !isDocumentCreator &&
        currentTeamPossession &&
        isDocumentStatusMatch(DocumentStatusEnum.REVIEWING) &&
        hasUserEditPermission
      );

    case ModalActionsEnum.INTERNAL_SEND_FOR_SIGNING:
      return (
        isDocumentCreator &&
        currentTeamPossession &&
        (isDocumentStatusMatch(DocumentStatusEnum.DRAFT) ||
          isDocumentStatusMatch(DocumentStatusEnum.REVIEWING) ||
          isDocumentStatusMatch(DocumentStatusEnum.UPLOADED) ||
          isDocumentStatusMatch(DocumentStatusEnum.SHARED)) &&
        hasUserEditPermission
      );

    case ModalActionsEnum.SEND_EXECUTED:
      return (
        isDocumentCreator &&
        isDocumentStatusMatch(DocumentStatusEnum.EXECUTED) &&
        hasUserInvitePermission &&
        !receiverTeam
      );

    case ModalActionsEnum.GRANT_ACCESS:
      return (
        isDocumentCreator &&
        isDocumentStatusMatch(DocumentStatusEnum.UPLOADED) &&
        hasUserInvitePermission &&
        !receiverTeam
      );

    case ModalActionsEnum.INTERNAL_APPROVAL:
      const isReviewingDocumentAvailableToBeSentForInternalApproval =
        hasUserEditPermission && currentTeamPossession && isDocumentStatusMatch(DocumentStatusEnum.REVIEWING);
      const isDraftDocumentAvailableToBeSentForInternalApproval =
        hasUserEditPermission &&
        isDocumentCreator &&
        (isDocumentStatusMatch(DocumentStatusEnum.DRAFT) ||
          isDocumentStatusMatch(DocumentStatusEnum.UPLOADED) ||
          isDocumentStatusMatch(DocumentStatusEnum.SHARED));

      return (
        isReviewingDocumentAvailableToBeSentForInternalApproval || isDraftDocumentAvailableToBeSentForInternalApproval
      );

    case ModalActionsEnum.SEND_VIA_EMAIL:
      return hasUserEditPermission && isDocumentCreator;

    case ModalActionsEnum.CANCEL_SIGNING:
      return hasUserEditPermission && isDocumentCreator && isDocumentStatusMatch(DocumentStatusEnum.SIGNING);

    case ModalActionsEnum.SEND_REMINDER:
      return hasUserEditPermission && isDocumentCreator && isDocumentStatusMatch(DocumentStatusEnum.SIGNING);

    default:
      return false;
  }
};
